import {
  Competency_Map_Status,
  CompetencyMapQuery,
  useCreateOrUpdateCompetencyMapMutation,
  useCreateOrUpdateKnowledgeBlockMutation,
  useCreateOrUpdateSkillMutation,
  useDeleteSkillMutation,
  useUpdateCompetencyMapStatusMutation,
} from "@generated/graphql";
import { SelectChangeEvent } from "@mui/material";
import { ISelectItem } from "components/inputs/Select";
import ROUTES from "constants/routes";
import { CompanyContext } from "context/companyContext";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import { cleanSpaceInString } from "helpers/validation/createValidation";
import useUpdateCompetencyMapStatus from "hooks/useUpdateCompetencyMapStatus";
import { ConstructorTabContext } from "pages/CompetencyMaps/view/context";
import { Dispatch, SetStateAction, useContext } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { CompetencyLiteralType, CompetencyMapInputs, KnowledgeBlockCompetencyOption, SkillOption } from "../../types";

interface KnowledgeBlockCompetencyWithFieldKey extends KnowledgeBlockCompetencyOption {
  fieldKey: CompetencyLiteralType;
}

interface SkillOptionWithFieldKey extends SkillOption {
  fieldKey: CompetencyLiteralType;
  order: number;
  knowledgeBlockId: string;
}

interface UseControllerProps {
  competencyMap: CompetencyMapQuery["competencyMap"];
  isCreate?: boolean;
  setIsEditable?: Dispatch<SetStateAction<boolean>>;
}

type SubmitOptions = {
  skillsIdsToDelete?: string[];
};

const useCompetencyMapController = ({
  competencyMap,
  isCreate = false,
  setIsEditable = () => undefined,
}: UseControllerProps) => {
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const { currentCompanyId } = useContext(CompanyContext);
  const { skillsIdsToDelete, setSkillsIdsToDelete } = useContext(ConstructorTabContext);
  const { mapId } = useParams();
  const form = useFormContext<CompetencyMapInputs>();
  const {
    formState: { errors },
  } = form;

  const [updateMapStatus] = useUpdateCompetencyMapStatusMutation();
  const [deleteSkill] = useDeleteSkillMutation();
  const [updateMap, { loading }] = useCreateOrUpdateCompetencyMapMutation();
  const [updateKnowledgeBlock, { loading: knowledgeBlockLoading }] = useCreateOrUpdateKnowledgeBlockMutation();
  const [updateSkill, { loading: skillLoading }] = useCreateOrUpdateSkillMutation();
  const { handleArchiveCompetencyMap } = useUpdateCompetencyMapStatus({
    competencyMap,
  });

  const handleChangeStatus = (event: SelectChangeEvent<ISelectItem>) => {
    if (!competencyMap?.id) return;

    const status = event.target.value as Competency_Map_Status;

    if (status === Competency_Map_Status.Archive) {
      handleArchiveCompetencyMap();
    } else {
      updateMapStatus({
        variables: {
          input: {
            id: competencyMap.id,
            status,
          },
        },
      })
        .then(() => {
          addToast({ type: ToastTypeEnum.SUCCESS });
        })
        .catch((e) => parseApolloError(e, { addToast }));
    }
  };

  const initialSkillsOrderByKnowledgeBlock = (competencyMap?.competences || []).reduce<Record<string, string[]>>(
    (acc, competence) => {
      competence.knowledgeBlockCompetences?.forEach((knowledgeBlockCompetence) => {
        const knowledgeBlockId = knowledgeBlockCompetence.knowledgeBlock.id;
        acc[knowledgeBlockId] = (knowledgeBlockCompetence.knowledgeBlock.skills || []).map((skill) => skill.id);
      });

      return acc;
    },
    {}
  );

  const handleUpdateKnowledgeBlocks = async (formData: CompetencyMapInputs) => {
    const { knowledgeBlocks } = formData;

    const createdKnowledgeBlocksWithKey = knowledgeBlocks?.reduce<KnowledgeBlockCompetencyWithFieldKey[]>(
      (blockAcc, block, blockIndex) => [
        ...blockAcc,
        ...(block.knowledgeBlockCompetences
          ? block.knowledgeBlockCompetences
              .map((competence, competenceIndex) => ({
                ...competence,
                fieldKey:
                  `knowledgeBlocks.${blockIndex}.knowledgeBlockCompetences.${competenceIndex}` as CompetencyLiteralType,
              }))
              .filter((competence) => !competence.id || form.getFieldState(competence.fieldKey).isDirty)
          : []),
      ],
      []
    );

    const promises = createdKnowledgeBlocksWithKey?.map((block) => {
      const blockTitle = cleanSpaceInString(block.title || "");

      return updateKnowledgeBlock({
        variables: {
          input: {
            companyId: currentCompanyId!,
            id: block?.id,
            title: blockTitle,
            profession: `${formData.title}-${blockTitle}`,
          },
        },
      })
        .then((res) => {
          form.setValue(`${block.fieldKey}.id`, res.data?.createOrUpdateKnowledgeBlock.id);
          return true;
        })
        .catch((e) => {
          parseApolloError(e, { addToast });
          return false;
        });
    });

    const results = await Promise.all(promises || []);
    return results.every(Boolean);
  };

  const handleUpdateSkills = async (knowledgeBlocks: NonNullable<CompetencyMapInputs["knowledgeBlocks"]>) => {
    const createdSkillsWithKey = knowledgeBlocks.reduce<SkillOptionWithFieldKey[]>(
      (blockAcc, block, blockIndex) => [
        ...blockAcc,
        ...(block.knowledgeBlockCompetences
          ? block.knowledgeBlockCompetences.reduce<SkillOptionWithFieldKey[]>(
              (competenceAcc, competence, competenceIndex) => [
                ...competenceAcc,
                ...(competence.skills
                  ? competence.skills
                      .map((skill, skillIndex) => ({
                        ...skill,
                        order: skillIndex,
                        fieldKey:
                          // eslint-disable-next-line max-len
                          `knowledgeBlocks.${blockIndex}.knowledgeBlockCompetences.${competenceIndex}.skills.${skillIndex}` as CompetencyLiteralType,
                        knowledgeBlockId: competence.id!,
                      }))
                      .filter((skill) => {
                        if (!skill.id) return true;

                        const initialOrder = initialSkillsOrderByKnowledgeBlock[skill.knowledgeBlockId];
                        if (!initialOrder) return false;

                        const initialSkillIndex = initialOrder.indexOf(skill.id);
                        return initialSkillIndex !== -1 && initialSkillIndex !== skill.order;
                      })
                  : []),
              ],
              []
            )
          : []),
      ],
      []
    );

    const promises = createdSkillsWithKey.map((skill) =>
      updateSkill({
        variables: {
          input: {
            id: skill.id,
            order: skill.order,
            title: cleanSpaceInString(skill.title),
            knowledgeBlockId: skill.knowledgeBlockId,
          },
        },
      })
        .then((res) => {
          form.setValue(`${skill.fieldKey}.id`, res.data?.createOrUpdateSkill.id);
          return true;
        })
        .catch((e) => {
          parseApolloError(e, { addToast });
          return false;
        })
    );

    const results = await Promise.all(promises);
    return results.every(Boolean);
  };

  const handleDeleteSkills = async (skillIds: string[] = skillsIdsToDelete) => {
    const promises = skillIds.map(async (skillId) => {
      return deleteSkill({
        variables: {
          skillId,
        },
      })
        .then(() => {
          setSkillsIdsToDelete((prev) => prev.filter((id) => id !== skillId));
          return true;
        })
        .catch((e) => {
          parseApolloError(e, { addToast });
          return false;
        });
    });

    const results = await Promise.all(promises);
    return results.every(Boolean);
  };

  const submitCompetencyMap = async (formData: CompetencyMapInputs, options?: SubmitOptions) => {
    let { knowledgeBlocks } = formData;

    const isDeleteSkillsSuccess = await handleDeleteSkills(options?.skillsIdsToDelete);
    if (!isDeleteSkillsSuccess) return false;

    const isUpdateKnowledgeBlocksSuccess = await handleUpdateKnowledgeBlocks(formData);
    if (!isUpdateKnowledgeBlocksSuccess) return false;
    knowledgeBlocks = form.getValues().knowledgeBlocks;

    const isUpdateSkillsSuccess = await handleUpdateSkills(knowledgeBlocks || []);
    if (!isUpdateSkillsSuccess) return false;
    knowledgeBlocks = form.getValues().knowledgeBlocks;

    try {
      const res = await updateMap({
        variables: {
          input: {
            companyId: currentCompanyId!,
            title: cleanSpaceInString(formData.title),
            id: mapId,
            specialization: cleanSpaceInString(formData.specialization),
            comment: formData.comment ? cleanSpaceInString(formData.comment) : undefined,
            competences: knowledgeBlocks!.map((block) => ({
              id: block.id,
              title: block.title!,
              knowledgeBlocksIds: (block.knowledgeBlockCompetences?.map((competence) => competence.id) ||
                []) as string[],
            })),
            isRepositoryCheck: formData.isRepositoryCheck,
          },
        },
      });

      if (isCreate)
        navigate(`/${ROUTES.COMPETENCE_MAPS}/view/${res.data?.createOrUpdateCompetencyMap.id}`, { replace: true });
      setIsEditable(false);
      addToast({ type: ToastTypeEnum.SUCCESS });
      return true;
    } catch (e) {
      parseApolloError(e, { addToast });
      return false;
    }
  };

  const onSubmit = async (formData: CompetencyMapInputs) => {
    await submitCompetencyMap(formData);
  };

  const onError = () => {
    const firstError = Object.values(errors)?.[0];
    const errorText = firstError?.message || firstError?.root?.message;
    addToast({ type: ToastTypeEnum.ERROR, text: errorText as string | undefined });
  };

  return {
    handleChangeStatus,
    onSubmit,
    onSubmitWithOptions: submitCompetencyMap,
    onError,
    loading,
    knowledgeBlockLoading,
    skillLoading,
  };
};

export default useCompetencyMapController;
